import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import {
  DailyVideos,
  TopicQuestions,
  MoreResources,
  SourceResources,
} from 'chameleon/ui-stack/ui/units/subunit-content';
import Body from 'chameleon/ui-stack/ui/page/body';
import ResourceTypes from 'legacy-packages/unit-outline/src/constants';
import { mapAssignmentsToResourceIds } from 'chameleon/ui-stack/ui/units/utils';
import { TeacherProgressChecks } from 'chameleon/ui-stack/ui/units/subunit-content/ppc';
import { TeacherPracticeProblems } from 'chameleon/ui-stack/ui/units/subunit-content/spp';
import SubunitsListNumberDisplay from 'chameleon/ui-stack/ui/units/subunits-list/components/SubunitsListNumberDisplay';
import AssignAllVideos from 'chameleon-packages/units/components/actions/AssignAllVideos';
import get from 'lodash/get';

export const RESOURCE_TYPE_KEYS = {
  videos: 'videos',
  'topic-questions': 'topic-questions',
  'progress-checks': 'progress-checks',
  'practice-exams': 'practice-exams',
  'practice-problems': 'practice-problems',
  sources: 'sources',
  more: 'more',
  'teacher-resources': 'teacher-resources',
  'student-resources': 'student-resources',
  assessments: 'assessments',
  'additional-unit-resources': 'additional-unit-resources',
};

const SubunitTypes = (props) => {
  const {
    resourceType,
    unit,
    subunits,
    widgetTagColumn,
    unitsLabel,
    subjectData,
    teacherAssignments,
    assignAllVideos,
    subunitLabel,
  } = props;
  const {
    displayName: unitDisplayName,
    number: unitNumber,
    title: unitTitle,
    unitId,
  } = unit;

  const STUDENT_RESOURCES = [
    'Student Handout',
    'Student Resources',
    'Student Reader',
  ];
  const TEACHER_RESOURCES = ['Teacher Resource'];

  const CONFIG_BY_TYPE = {
    [RESOURCE_TYPE_KEYS.videos]: {
      bg: 'bg-purple-600',
      color: 'white',
      icon: 'daily_video',
      label: 'AP Daily Videos',
      resourceTypes: [ResourceTypes.EMBEDDED_VIDEO],
      Component: DailyVideos,
      AssignAllComponent: assignAllVideos ? AssignAllVideos : null,
      otherProps: {
        subjectData,
        assignAllVideos,
      },
    },
    [RESOURCE_TYPE_KEYS['topic-questions']]: {
      bg: 'bg-yellow-400',
      color: 'black',
      icon: 'topic_questions',
      label: 'Topic Questions',
      resourceTypes: [ResourceTypes.PRACTICE_QUESTIONS],
      Component: TopicQuestions,
      otherProps: {
        unitNumber,
        unitTitle,
        widgetTagColumn,
        unitsLabel,
      },
    },
    [RESOURCE_TYPE_KEYS.sources]: {
      bg: 'bg-blue-300',
      color: 'black',
      icon: 'more_resources',
      label: 'Sources',
      resourceTypes: [ResourceTypes.SOURCE, ResourceTypes.LTI],
      Component: SourceResources,
      otherProps: {
        masterSubjectId: subjectData.id,
      },
    },
    [RESOURCE_TYPE_KEYS['practice-problems']]: {
      bg: 'bg-teal-100',
      color: 'black',
      icon: 'spp',
      label: 'Student Practice',
      className: 'fill-current text-teal',
      resourceTypes: [ResourceTypes.SPP],
      Component: (props) => {
        const { items: resources, ...rest } = props;
        const getTeacherAssignments = get(
          teacherAssignments,
          'assignments',
          [],
        );
        return (
          <TeacherPracticeProblems
            assignments={getTeacherAssignments}
            resources={resources}
            {...rest}
            showRightContent={false}
          />
        );
      },
      otherProps: {
        unitNumber,
        unitTitle,
        unitId,
        teacherAssignments,
      },
    },
    [RESOURCE_TYPE_KEYS['progress-checks']]: {
      bg: 'bg-blue-500',
      color: 'white',
      icon: 'progress_checks',
      iconClassName: 'text-blue-200',
      label: 'Progress Checks',
      resourceTypes: [ResourceTypes.ASSESSMENT],
      Component: (props) => {
        const { items: resources, teacherAssignments, ...rest } = props;
        return (
          <TeacherProgressChecks
            assignments={mapAssignmentsToResourceIds(
              teacherAssignments,
              resources,
            )}
            resources={resources}
            {...rest}
            showRightContent={false}
          />
        );
      },
      otherProps: {
        unitNumber,
        unitTitle,
        unitId,
        teacherAssignments,
      },
    },
    [RESOURCE_TYPE_KEYS['practice-exams']]: {
      bg: 'bg-green-100',
      color: 'black',
      icon: 'practice-exam',
      iconClassName: 'text-green-100',
      label: 'Practice Exams',
      resourceTypes: [ResourceTypes.ASSESSMENT],
      Component: (props) => {
        const { items: resources, teacherAssignments, ...rest } = props;
        return (
          <TeacherProgressChecks
            assignments={mapAssignmentsToResourceIds(
              teacherAssignments,
              resources,
            )}
            resources={resources}
            {...rest}
            showRightContent={false}
          />
        );
      },
      otherProps: {
        unitNumber,
        unitTitle,
        teacherAssignments,
        unitId,
      },
    },
    [RESOURCE_TYPE_KEYS.more]: {
      bg: 'bg-blue-300',
      color: 'black',
      icon: 'more_resources',
      label: 'More Resources',
      resourceTypes: [ResourceTypes.URL],
      Component: MoreResources,
    },
    [RESOURCE_TYPE_KEYS['teacher-resources']]: {
      bg: 'bg-blue-300',
      color: 'black',
      icon: 'more_resources',
      label: 'Teacher Resources',
      resourceTypes: [ResourceTypes.URL],
      Component: (props) => {
        const { items: resources } = props;
        const filteredItems = resources.filter((item) =>
          TEACHER_RESOURCES.includes(item.displayName),
        );
        return <MoreResources {...props} items={filteredItems} />;
      },
    },
    [RESOURCE_TYPE_KEYS['student-resources']]: {
      bg: 'bg-blue-300',
      color: 'black',
      icon: 'more_resources',
      label: 'Student Resources',
      resourceTypes: [ResourceTypes.URL],
      Component: (props) => {
        const { items: resources } = props;
        const filteredItems = resources.filter((item) =>
          STUDENT_RESOURCES.includes(item.displayName),
        );
        return <MoreResources {...props} items={filteredItems} />;
      },
    },
    [RESOURCE_TYPE_KEYS.assessments]: {
      bg: 'bg-blue-300',
      color: 'black',
      icon: 'more_resources',
      label: 'Assessments',
      resourceTypes: [ResourceTypes.ASSESSMENT],
      Component: (props) => {
        const { items: resources, teacherAssignments, ...rest } = props;
        return (
          <TeacherProgressChecks
            assignments={mapAssignmentsToResourceIds(
              teacherAssignments,
              resources,
            )}
            resources={resources}
            {...rest}
            showRightContent={false}
          />
        );
      },
    },
    [RESOURCE_TYPE_KEYS['additional-unit-resources']]: {
      bg: 'bg-blue-300',
      color: 'black',
      icon: 'more_resources',
      label: 'Additional Unit Resources',
      resourceTypes: [
        ResourceTypes.URL,
        ResourceTypes.PARTNER_LINK,
        ResourceTypes.DISTANCE_LINK,
      ],
      Component: (props) => {
        const { items: resources } = props;
        const filteredItems = resources.filter((item) => {
          const resourcesToHide = TEACHER_RESOURCES.concat(STUDENT_RESOURCES);
          const shouldShow = !resourcesToHide.includes(item.displayName);
          return shouldShow;
        });
        return <MoreResources {...props} items={filteredItems} />;
      },
    },
  };

  const {
    bg,
    color,
    icon,
    iconClassName,
    label,
    resourceTypes,
    Component,
    AssignAllComponent,
    otherProps = {},
  } = CONFIG_BY_TYPE[resourceType];
  const className = bg;

  const resourcesBySubunits = useMemo(
    () =>
      subunits
        .map((subunit) => {
          const { resources } = subunit;
          const filteredResources = resources.filter(({ __typename }) =>
            resourceTypes.includes(__typename),
          );
          if (
            filteredResources[0] &&
            filteredResources[0].__typename === ResourceTypes.PRACTICE_QUESTIONS
          ) {
            const assignableTopicQuestions = (
              filteredResources[0].questions || []
            ).filter((item) => item.hasAllTopicsCovered === true);
            return {
              ...subunit,
              resources: assignableTopicQuestions,
            };
          }

          return {
            ...subunit,
            resources: filteredResources,
          };
        })
        .filter(({ resources }) => resources.length > 0),
    [subunits, resourceType],
  );

  const allResources = useMemo(
    () =>
      resourcesBySubunits.reduce((acc, subunit) => {
        acc.push(...subunit.resources);
        return acc;
      }, []),
    [resourcesBySubunits],
  );

  const assignAllComponentProps = {
    containerClasses: 'mt-8.4 text-black',
    items: allResources,
    unitNumber,
    isButtonInverted: true,
    assignAll: true,
    unitTitle: unitDisplayName,
    context: 'unit',
  };

  return (
    <Fragment>
      <div className={cn(className, 'px-8.4')}>
        <div className="py-[16px] flex items-center">
          <Icon
            className={cn('mr-8.4', iconClassName)}
            id={icon}
            width="80"
            height="80"
          />
          <div className={`text-22 font-medium text-${color}`}>
            {unitDisplayName} {label}
            {AssignAllComponent && (
              <AssignAllComponent {...assignAllComponentProps} />
            )}
          </div>
        </div>
      </div>
      <Body>
        <div className="divide-y divide-solid divide-gray-400 border-b border-gray-300">
          {resourcesBySubunits.map((subunit, index) => {
            const {
              displayName,
              resources,
              metadata,
              number: subunitNumber,
              displayNumber,
              type,
            } = subunit;
            let componentProps = {
              ...otherProps,
              showIcon: false,
              items: resources,
              title: displayName,
              unitNumber,
              unitId,
              unitTitle: unitDisplayName,
              subunitNumber,
              subunitTitle: displayName,
              subunitLabel,
              teacherAssignments,
              type,
            };

            if (unitNumber > 0 && displayNumber > 0) {
              componentProps.label = (
                <SubunitsListNumberDisplay
                  unitNumber={unitNumber}
                  displayNumber={displayNumber}
                />
              );
            }

            if (resourceType === RESOURCE_TYPE_KEYS['topic-questions']) {
              componentProps = {
                ...componentProps,
                metadata,
                subunitTitle: displayName,
              };
            }

            return <Component key={`component-${index}`} {...componentProps} />;
          })}
        </div>
      </Body>
    </Fragment>
  );
};

SubunitTypes.propTypes = {
  resourceType: PropTypes.string.isRequired,
  unit: PropTypes.object.isRequired,
  subunits: PropTypes.array.isRequired,
  widgetTagColumn: PropTypes.string,
  unitsLabel: PropTypes.string,
  subjectData: PropTypes.object.isRequired,
  assignAllVideos: PropTypes.bool,
  subunitLabel: PropTypes.string.isRequired,
};

SubunitTypes.defaultProps = {
  widgetTagColumn: '',
  unitsLabel: '',
  assignAllVideos: false,
};

export default SubunitTypes;
